import { h, render, Fragment, FunctionComponent, options, createContext } from 'preact';
import { useState, useEffect, StateUpdater, useContext } from 'preact/hooks';
import { useFetch } from '../hooks/useFetch';
import MicroModal from 'micromodal';
import { getApi } from '../../../src/ts/libs/getApi';
import { currency } from '../../../src/ts/libs/currency';
import { BasketItemProps, BasketProps } from '../types/basket';
import { Error } from './../error/Error';
import { Alert } from '../common/Alert';

interface SellingUnitProps {
  id: string;
  name: string;
  active: boolean;
}

const Basket = createContext<BasketProps>({});
const BasketEditItem: FunctionComponent<Pick<BasketItemProps, 'quantity' | 'alternateSellingUnits' | 'id'>> = ({
  quantity,
  alternateSellingUnits,
  id,
}) => {
  const { setBasket } = useContext(Basket);
  const [qty, setQuantity] = useState(+quantity);
  const {
    response,
    fetchData: updateQuantity,
    error: updateQuanityError,
  } = useFetch(`${getApi('updateBasketQuantity')}`, {});
  const {
    response: responseSu,
    fetchData: updateSellingUnit,
    error: updateSellingUnitError,
  } = useFetch(`${getApi('updateSellingUnit')}`, {});
  const [sellingUnitId, setSellingUnitId] = useState(alternateSellingUnits.id);
  // if we updat quantity then update basket

  useEffect(() => {
    if (response && response.hasOwnProperty('items')) {
      setBasket(() => ({ ...response }));
    }
  }, [response]);
  useEffect(() => {
    if (responseSu && responseSu.hasOwnProperty('items')) {
      setBasket(() => ({ ...responseSu }));
    }
  }, [responseSu]);

  const handleChangeOne = (evt) => {
    const { value } = evt.currentTarget;

    updateQuantity(
      {
        method: 'PUT',
      },
      {
        productCode: id,
        quantity: value,
        sellingUnitId: sellingUnitId,
      }
    );

    setQuantity(value);
  };

  const handleRemoveOne = () => {
    // set the value first
    setQuantity((c) => {
      const newVal = c === 1 ? 1 : +c - 1;

      // update api
      updateQuantity(
        {
          method: 'PUT',
        },
        {
          productCode: id,
          quantity: newVal,
          sellingUnitId: sellingUnitId,
        }
      );

      const event = new Event('basket-update');
      window.dispatchEvent(event);
      const eventRemove = new Event('basket-remove-item');
      window.dispatchEvent(eventRemove);

      // optimistically set qunatity
      return newVal;
    });
  };
  const handleAddOne = () => {
    // set the value first
    setQuantity((c) => {
      const newVal = +c + 1;

      // update api
      updateQuantity(
        {
          method: 'PUT',
        },
        {
          productCode: id,
          quantity: newVal,
          sellingUnitId: sellingUnitId,
        }
      );

      // optimistically set qunatity
      return newVal;
    });
  };
  const handleChangeUnit = (e) => {
    const old = sellingUnitId;

    setSellingUnitId(e.currentTarget.value);

    updateSellingUnit(
      {
        method: 'PUT',
      },
      {
        previousSellingUnitId: old,
        newSellingUnitId: e.currentTarget.value,
      }
    );

    const eventRemove = new Event('basket-remove-item');
    window.dispatchEvent(eventRemove);
  };

  return (
    <div className="basket-card__form is-active" data-test="basket-edit-item">
      {updateQuanityError && <strong>Sorry something has gone wrong</strong>}
      <form className="form">
        <div className="form-field">
          <label htmlFor="quantity">Quantity</label>
          <div className="quantity-control">
            <button
              type="button"
              className="button button--muted"
              onClick={handleRemoveOne}
              data-test="basket-edit-remove-one"
            >
              <span className="button__label  sr-only">Minus one from quantity</span>
              <svg aria-hidden="true" width={12} height={4} xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M11.333 3.037c0 .244-.2.444-.444.444H1.11a.446.446 0 01-.444-.444V.963c0-.244.2-.444.444-.444h9.778c.244 0 .444.2.444.444v2.074z"
                  fill="currentColor"
                  fillRule="evenodd"
                />
              </svg>
            </button>
            <input
              name="quantity"
              id="quantity"
              value={qty}
              type="number"
              onChange={handleChangeOne}
              aria-label="Enter Quanity"
              data-test="basket-edit-quantity"
            />
            <button
              type="button"
              className="button button--muted"
              onClick={handleAddOne}
              data-test="basket-edit-add-one"
            >
              <span className="button__label  sr-only">Plus one to quantity</span>
              <svg aria-hidden="true" width={12} height={12} xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M11.333 7.037c0 .244-.2.444-.444.444H7.48v3.408c0 .244-.2.444-.444.444H4.963a.446.446 0 01-.444-.444V7.48H1.11a.446.446 0 01-.444-.444V4.963c0-.244.2-.444.444-.444H4.52V1.11c0-.244.2-.444.444-.444h2.074c.244 0 .444.2.444.444V4.52h3.408c.244 0 .444.2.444.444v2.074z"
                  fill="currentColor"
                  fillRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>

        {alternateSellingUnits?.sellingUnits && alternateSellingUnits?.sellingUnits.length >= 1 && (
          <div className="form-field">
            <label htmlFor="sellingUnit">Selling unit</label>

            <select
              data-test="mini-cart-item-change-selling-unit"
              name="sellingUnit"
              id="sellingUnit"
              className="select"
              onChange={handleChangeUnit}
              value={sellingUnitId}
            >
              {alternateSellingUnits?.sellingUnits.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </form>
    </div>
  );
};
const BasketCard: FunctionComponent<BasketItemProps> = ({
  name,
  price,
  code,
  unitPrice,
  quantity,
  img,
  id,
  url,
  alternateSellingUnits,
}) => {
  const [open, setOpen] = useState(false);

  const { setBasket } = useContext(Basket);
  const { response, fetchData: removeItem, error: removeItError } = useFetch(`${getApi('removeFromBasket')}`, {});
  const handleRemove = async (productCode) => {
    await removeItem(
      {
        method: 'DELETE',
      },
      {
        productCode,
        sellingUnitId: alternateSellingUnits?.id,
      }
    );

    const eventRemove = new Event('basket-remove-item');
    window.dispatchEvent(eventRemove);
  };

  useEffect(() => {
    if (response && response.hasOwnProperty('items')) {
      setBasket(() => ({ ...response }));
    }
  }, [response]);
  const { name: sellingUnit } = alternateSellingUnits?.sellingUnits?.find((u) => u?.active);

  const isSOS = code === 'SOS';
  return (
    <div
      className={isSOS ? 'basket-card basket-card--no-img basket-card--reverse' : 'basket-card'}
      data-test="basket-close-basket-item"
    >
      <picture className="basket-card__img">
        <img src={img ? img : '/assets/img/no-photo-product.png'} alt={name} />
      </picture>

      <div className="basket-card__content">
        <div className="basket-card__body">
          <div className="basket-card__header">
            <h3 className="basket-card__heading">
              {!isSOS && url ? <a href={url}>{name}</a> : <Fragment>{name}</Fragment>}
            </h3>
            {!isSOS && (
              <div className="basket-card__remove">
                <button
                  type="button"
                  className="button button--transparent"
                  onClick={() => handleRemove(id)}
                  data-test="basket-remove-item"
                >
                  <span className="button__label button__label--underline sr-only">Remove item</span>
                  <svg aria-hidden="true" width={14} height={16} xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M13.5 1H9.75L9.456.416A.75.75 0 008.784 0H5.212a.741.741 0 00-.668.416L4.25 1H.5a.5.5 0 00-.5.5v1a.5.5 0 00.5.5h13a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5zM1.662 14.594A1.5 1.5 0 003.16 16h7.682a1.5 1.5 0 001.497-1.406L13 4H1l.663 10.594z"
                      fill="currentColor"
                      fillRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            )}
            {removeItError && <strong>Sorry something has gone wrong</strong>}
          </div>
          {!isSOS && (
            <ul className="basket-card__meta">
              {code && <li data-test="mini-cart-item-quantity-code">Code: {code}</li>}
              {unitPrice && <li data-test="mini-cart-item-unit-price">Unit price: {currency(+unitPrice)}</li>}
              {quantity && <li data-test="mini-cart-item-quantity">Quantity: {quantity}</li>}

              {sellingUnit && <li data-test="mini-cart-item-quantity-selling-unit">Selling unit: {sellingUnit}</li>}
            </ul>
          )}
          {isSOS && (
            <div className="basket-card__msg" data-test="mini-basket-sos-msg">
              You will be charged a small order handling fee of £12.00 if your order is less than £150 (excluding VAT).
            </div>
          )}
          {!isSOS && open && (
            <BasketEditItem alternateSellingUnits={alternateSellingUnits} quantity={quantity} id={id} />
          )}
        </div>
        <div className="basket-card__footer">
          {!isSOS ? (
            <button
              type="button"
              className={`button basket-blade__actions__cont button--transparent ${
                open ? 'is-active' : 'is-not-active'
              }`}
              onClick={() => setOpen((o) => !o)}
              data-test="basket-close-basket-item-edit"
            >
              <span className="button__label button__label--underline">{open ? 'Hide' : 'Edit'}</span>
              <svg aria-hidden="true" width={8} height={6} xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M4.425 5.49l3.399-3.4a.597.597 0 000-.846L7.259.679a.597.597 0 00-.847 0L4 3.085 1.59.676a.597.597 0 00-.846 0l-.568.565a.597.597 0 000 .847l3.4 3.4a.598.598 0 00.849.002z"
                  fill="#25252D"
                  fillRule="evenodd"
                />
              </svg>
            </button>
          ) : (
            <span></span>
          )}
          <span className="basket-card__total">
            <strong data-test="mini-cart-total-amount">{currency(+price)}</strong> Ex VAT
          </span>
        </div>
      </div>
    </div>
  );
};
const Blade = ({ items, total, error, basketQty, restrictedProduct }) => (
  <div className="modal modal--blade modal--blade--right micromodal-slide" id="cart-blade" aria-hidden="true">
    <div className="modal__overlay" tabIndex={-1} data-micromodal-close>
      <div className="modal__container " role="dialog" aria-modal="true" aria-labelledby="cart-blade__heading">
        <header className="modal__header">
          <button
            type="button"
            className="button button--transparent modal__close"
            aria-label="Close modal"
            data-micromodal-close
            data-test="basket-close-basket-top"
          >
            <span className="button__label">Close</span>
            <svg aria-hidden="true" width={24} height={24} xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 0C5.371 0 0 5.371 0 12s5.371 12 12 12 12-5.371 12-12S18.629 0 12 0zm5.884 15.15a.581.581 0 010 .823l-1.916 1.91a.581.581 0 01-.823 0L12 14.71l-3.15 3.174a.581.581 0 01-.823 0l-1.91-1.916a.581.581 0 010-.823L9.29 12 6.116 8.85a.581.581 0 010-.823l1.916-1.916a.581.581 0 01.823 0L12 9.291l3.15-3.175a.581.581 0 01.823 0l1.916 1.916a.581.581 0 010 .823L14.709 12l3.175 3.15z"
                fill="#25252D"
                fillRule="evenodd"
              />
            </svg>
          </button>
          <div className="modal__heading-wrap">
            <h2 className="modal__heading" id="basket-blade__heading">
              Your basket
            </h2>
            <span data-test="basket-total-quanity">({basketQty ? basketQty : '0'})</span>
          </div>
        </header>

        <main className="modal__body" id="basket-blade-content">
          {restrictedProduct && (
            <Alert message="Please note: A license is required to purchase some products in your basket." />
          )}
          {items?.length >= 1 && items.map((item) => <BasketCard key={item.id} {...item} />)}
          {error && <Error message="Cannot fetch basket" />}
        </main>

        <footer className="modal__footer">
          <div className="basket-blade__totals">
            <h4 className="basket-blade__totals__label">Sub total</h4>
            <p className="basket-blade__totals__value">{currency(+total)}</p>
          </div>
          <div className="basket-blade__actions">
            <a
              href="#"
              className="button basket-blade__actions__cont button--transparent"
              data-test="basket-close-mini-basket"
              data-micromodal-close
            >
              <svg aria-hidden="true" width={6} height={8} xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M.51 3.575L3.91.176a.597.597 0 01.846 0l.565.565a.597.597 0 010 .847L2.915 4l2.409 2.41a.597.597 0 010 .846l-.565.568a.597.597 0 01-.847 0l-3.4-3.4a.598.598 0 01-.002-.849z"
                  fill="currentColor"
                  fillRule="evenodd"
                />
              </svg>
              <span className="button__label  ">Continue shopping</span>
            </a>
            {items?.length >= 1 && (
              <a href="/your-order" className="button button--lg" data-test="basket-open-mini-basket">
                <span className="button__label  ">Review your order</span>
                <svg aria-hidden="true" width={8} height={12} xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M7.32 5.434L2.788.902a.796.796 0 00-1.13 0l-.753.753a.796.796 0 000 1.13L4.114 6 .902 9.212a.796.796 0 000 1.13l.753.756a.796.796 0 001.13 0l4.531-4.532a.797.797 0 00.004-1.132z"
                    fill="currentColor"
                    fillRule="evenodd"
                  />
                </svg>
              </a>
            )}
          </div>
        </footer>
      </div>
    </div>
  </div>
);

const MiniCart = () => {
  const [basket, setBasket] = useState<BasketProps>({});
  const { response, error, loading, fetchData, error: getBasketError } = useFetch(`${getApi('getBasket')}`, {}, true);

  useEffect(() => {
    if (response && response.hasOwnProperty('items')) {
      setBasket(() => ({ ...response }));
    }
  }, [response]);

  useEffect(() => {
    (async function getBasket() {
      await fetchData({});
    })();
  }, []);
  const handleModalOpen = () => {
    MicroModal.show('cart-blade', { disableScroll: true });
  };
  const handleUpdateBasket = async () => {
    await fetchData({});

    handleModalOpen();
  };

  useEffect(() => {
    window?.addEventListener('basket-update', handleUpdateBasket);
    return () => {
      // Clean up the subscription
      window?.removeEventListener('basket-update', handleUpdateBasket);
    };
  }, []);

  const basketQty = basket?.items?.filter((item) => item.code !== 'SOS').length;

  return (
    <Basket.Provider value={{ ...basket, setBasket, basketQty }}>
      <button
        type="button"
        className="basket-blade-action"
        data-test="basket-open-mini-basket"
        onClick={() => handleModalOpen()}
      >
        <span className="basket-blade-action__icon">
          <svg viewBox="0 0 36 32" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M33.008 18.832l2.954-13A1.5 1.5 0 0034.5 4H9.95l-.572-2.8A1.5 1.5 0 007.908 0H1.5A1.5 1.5 0 000 1.5v1A1.5 1.5 0 001.5 4h4.368l4.39 21.465a3.5 3.5 0 104.19.535h13.103a3.5 3.5 0 103.975-.65l.345-1.518A1.5 1.5 0 0030.408 22H13.632l-.409-2h18.322a1.5 1.5 0 001.462-1.168z"
              fill="currentColor"
              fillRule="evenodd"
            />
          </svg>
          {(basketQty || loading) && (
            <span className={`basket-blade-action__count${loading ? ' is-loading' : ''}`}>{basketQty || '0'}</span>
          )}
        </span>
        {loading ? (
          <span className={`basket-blade-action__label${loading ? ' is-loading' : ''}`}></span>
        ) : (
          <span className="basket-blade-action__label">{currency(+basket?.total)}</span>
        )}
      </button>
      <Blade
        items={basket?.items}
        total={basket?.total}
        error={getBasketError}
        basketQty={basketQty}
        restrictedProduct={basket?.restrictedProduct}
      />
    </Basket.Provider>
  );
};
try {
  const domContainer = document.querySelector('#mini-cart');
  if (domContainer) {
    render(<MiniCart />, domContainer);
  }
} catch (e) {
  console.warn(e);
}
